import React, { useEffect, useMemo } from "react";
import {
  // Menu,
  Search,
  House,
  Users,
  MessageSquareMore,
  Notebook,
  Gamepad2,
  Ellipsis,
  // UserPen,
  Settings,
  LogOut,
  // CircleAlert,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
// import { UserMenu } from "@/components/blocks/user-menu";
import { DialogProfile } from "@/features/dialog-profile";
import { DialogSettings } from "@/features/dialog-settings";
import { useAuth } from "@/providers/auth-provider";
import { useTranslation } from "@/providers/language-provider";
import { signal } from "@preact/signals-react";
import { useSignals } from "@preact/signals-react/runtime";
import { NotificationButton } from "@/features/notification-button";
import { useChats } from "@/providers/chat-provider";
import { useNotes } from "@/providers/note-provider";
import useBackButton from "@/hooks/use-backbutton";
import { checkDisplayMode } from "@/lib/web-tools";
import versionData from "@/version.json";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { showMessageBox } from "@/components/blocks/message-box";

const localeText = {
  en: {
    exit_title: "Exit",
    exit_confirm: "Do you want to exit the app?",
  },
  ko: {
    exit_title: "종료",
    exit_confirm: "앱을 종료하시겠습니까?",
  },
} as const;

const styleNavItemMuted =
  "relative flex items-center gap-2 text-muted-foreground transition-colors hover:text-foreground cursor-pointer";

const styleNavItem =
  "relative flex items-center gap-2 text-foreground transition-colors hover:text-foreground cursor-pointer";

export const MAIN_MENUS = {
  HOME: "Home",
  FRIENDS: "Friends",
  CHATS: "Chats",
  NOTES: "Notes",
  PLAY: "Play",
  ETC: "Etc",
} as const;

export type MenuItem = (typeof MAIN_MENUS)[keyof typeof MAIN_MENUS];

export const mainMenu = signal(MAIN_MENUS.HOME as MenuItem);
export const setMainMenu = (item: MenuItem) => {
  mainMenu.value = item;
};

const profileUser = signal<any>(null);
const profileRef = signal<any>(null);
export const OpenProfile = (user: any) => {
  profileUser.value = user;
  profileRef.value?.open();
};

export function MainHeader() {
  useSignals();

  const { user, logout } = useAuth();
  const { t, language } = useTranslation();
  const { currentChat, selectChat, chatsHasNewMessage } = useChats();
  const { activeParent, moveToParent } = useNotes();
  const { enterChat } = useChats();

  useEffect(() => {
    // 디버깅을 위한 상세 로그
    console.log("[main] URL 디버깅:", {
      fullUrl: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      origin: window.location.origin,
    });

    const params = new URLSearchParams(window.location.search);
    if (params.get("source") !== "notification") return;

    const channelId = params.get("channelId");
    const chatId = params.get("chatId");

    console.log("[main] URL 파라미터 파싱:", {
      fullUrl: window.location.href,
      searchString: window.location.search,
      channelId,
      chatId,
      allParams: Array.from(params.entries()),
    });

    if (channelId === "CHAT_MESSAGE" && chatId) {
      console.log("[main] 채팅 선택 실행:", chatId);
      enterChat(chatId);
    } else {
      console.log("[main] 조건 불만족:", { channelId, chatId });
    }

    // URL을 깔끔하게 정리 (뒤로가기/새로고침 시 재진입 방지)
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, "", cleanUrl);
  }, []);

  const text = useMemo(() => {
    return localeText[language as keyof typeof localeText] ?? localeText["en"];
  }, [language]);

  useBackButton(() => {
    if (currentChat) {
      selectChat(null);
      return true;
    }

    if (activeParent) {
      moveToParent(activeParent);
      return true;
    }

    // PWA로 실행중인 경우에는 종료 여부를 물어봄
    const displayMode = checkDisplayMode();
    if (displayMode === "PWA_STANDALONE" || displayMode === "PWA_FULLSCREEN") {
      handleExit();
      return true; // 브라우저의 뒤로가기를 방지
    }

    return false; // 일반 브라우저에서는 기본 동작 실행
  }, [currentChat, selectChat, activeParent]);

  const handleExit = async () => {
    const result = await showMessageBox({
      type: "Confirm_Alert",
      title: text.exit_title,
      description: text.exit_confirm,
    });
    if (result === "Yes") {
      window.close();
    }
  };

  // useSignalEffect(() => {
  //   // 채팅이 아닌 다른 탭으로 이동 시 현태 채팅방에서 나감.
  //   if (mainMenu.value !== MAIN_MENUS.CHATS) {
  //     selectChat(null);
  //   }
  // });

  const dialogSettingsRef =
    React.useRef<React.ElementRef<typeof DialogSettings>>(null);
  const openDialogSettings = () => {
    if (dialogSettingsRef.current) {
      dialogSettingsRef.current.open();
    }
  };

  const renderMenuIcons = (item: MenuItem) => {
    switch (item) {
      case MAIN_MENUS.HOME:
        return <House className="h-5 w-5" />;
      case MAIN_MENUS.FRIENDS:
        return <Users className="h-5 w-5" />;
      case MAIN_MENUS.CHATS:
        return <MessageSquareMore className="h-5 w-5" />;
      case MAIN_MENUS.NOTES:
        return <Notebook className="h-5 w-5" />;
      case MAIN_MENUS.PLAY:
        return <Gamepad2 className="h-5 w-5" />;
      case MAIN_MENUS.ETC:
        return <Ellipsis className="h-5 w-5" />;
      default:
        return <Search className="h-5 w-5" />;
    }
  };

  const renderMenuItems = (textClass: string) => {
    return Object.values(MAIN_MENUS).map((item) => (
      <a
        key={item}
        // onClick={() => setMenu(item)}
        // className={menu === item ? styleNavItem : styleNavItemMuted}
        onClick={() => (mainMenu.value = item)}
        className={mainMenu.value === item ? styleNavItem : styleNavItemMuted}
        type="submit"
      >
        {renderMenuIcons(item)}
        <span className={textClass}>{t(item)}</span>
        {item === MAIN_MENUS.CHATS && chatsHasNewMessage && (
          <div className="absolute top-0 left-0 w-2 h-2 bg-red-500 rounded-full" />
        )}
      </a>
    ));
  };

  return (
    <header className="relative flex h-16 items-center gap-4 border-t bg-background px-4 md:px-6 overflow-hidden">
      <Sheet>
        <SheetTrigger asChild>
          {/* <Button variant="outline" size="icon" className="shrink-0 md:hidden">
            <Menu className="h-5 w-5" />
            <span className="sr-only">{t("NaviMenu")}</span>
          </Button> */}
          <Button variant="ghost" size="icon" className="p-0 rounded-lg">
            <Avatar className="h-8 w-8 rounded-lg">
              <AvatarImage
                src={user?.picture ?? "/images/anonymous-icon.jpg"}
                alt={"Profile Image"}
              />
              <AvatarFallback className="rounded-lg">N/A</AvatarFallback>
            </Avatar>
            <span className="sr-only">{t("NaviMenu")}</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="left">
          <div className="flex flex-col h-full">
            <SheetHeader>
              <SheetTitle>{t("Title")}</SheetTitle>
              <SheetDescription>{t("TitleDesc")}</SheetDescription>
              <div
                className="flex w-full items-center py-2 cursor-pointer"
                onClick={() => OpenProfile(null)}
              >
                <Avatar className="h-14 w-14 rounded-full">
                  <AvatarImage
                    src={user?.picture ?? "/images/anonymous-icon.jpg"}
                    alt={"Profile Image"}
                  />
                  <AvatarFallback className="rounded-full">N/A</AvatarFallback>
                </Avatar>
                <span className="ml-4 text-xl">
                  {user?.name ?? "Not logged in"}
                </span>
              </div>
            </SheetHeader>

            <nav className="flex-1 grid content-start mt-6 gap-6 font-medium">
              {renderMenuItems("")}
            </nav>
            <SheetFooter>
              <div className="flex flex-col w-full items-start gap-6">
                {/* 설정 버튼 */}
                <Button
                  variant="info"
                  className="w-full"
                  onClick={openDialogSettings}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  {t("Settings")}
                </Button>
                {/* 로그 아웃 버튼 */}
                <div className="flex w-full justify-between items-center">
                  <Button onClick={logout}>
                    <LogOut className="h-4 w-4 mr-2" />
                    {t("Logout")}
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    v.{versionData.VERSION}
                  </p>
                </div>
              </div>
            </SheetFooter>
          </div>
        </SheetContent>
      </Sheet>
      {/* <nav className="hidden flex-col gap-6 text-lg font-medium md:flex md:flex-row md:items-center md:gap-5 md:text-sm lg:gap-6 whitespace-nowrap"> */}
      <nav className="font-medium flex flex-1 items-center gap-5 lg:gap-6 text-sm whitespace-nowrap overflow-hidden">
        {renderMenuItems("hidden md:flex")}
      </nav>
      {/* <div className="whitespace-nowrap md:hidden">{t("Title")}</div> */}
      <div className="flex justify-end items-center gap-4 md:ml-auto md:gap-2 lg:gap-4">
        <NotificationButton />
        {/* <UserMenu
          userName={user?.name ?? "Not logged in"}
          userPicture={user?.picture ?? "/images/anonymous-icon.jpg"}
          menuItems={[
            {
              name: t("Profile"),
              icon: <UserPen className="h-5 w-5" />,
              onClick: () => OpenProfile(null),
            },
            {
              name: t("Settings"),
              icon: <Settings className="h-5 w-5" />,
              onClick: openDialogSettings,
              separator: true,
            },
            {
              name: t("Logout"),
              icon: <LogOut className="h-5 w-5" />,
              onClick: logout,
            },
          ]}
        /> */}
      </div>
      <DialogProfile
        user={profileUser.value}
        ref={(el) => (profileRef.value = el)}
      >
        {null}
        {/* <Button variant="outline">Settings</Button> */}
      </DialogProfile>
      <DialogSettings ref={dialogSettingsRef}>
        {null}
        {/* <Button variant="outline">Settings</Button> */}
      </DialogSettings>
    </header>
  );
}
